import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { apiFetch, logEvent } from '../api';

function AppealDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [appeal, setAppeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchAppeal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAppeal = async () => {
    try {
      const response = await apiFetch(`/get-appeal/${id}`);
      if (response.ok) {
        const data = await response.json();
        if (data.appeal) {
          setAppeal(data.appeal);
        }
      }
    } catch (error) {
      console.log('Error fetching appeal:', error);
    }
    setLoading(false);
  };

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await apiFetch(`/update-appeal/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        setAppeal({ ...appeal, status: newStatus });
      }
    } catch (error) {
      console.log('Update error:', error);
    }
    setUpdating(false);
  };

  const handleCopy = () => {
    const confirmed = window.confirm(
      'I confirm that a licensed healthcare professional has reviewed and validated this appeal text before use.'
    );
    if (!confirmed) return;
    navigator.clipboard.writeText(appeal.appeal_letter);
    logEvent('copy_appeal', appeal.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLogout = async () => {
    await logEvent('logout');
    await signOut();
    navigate('/login');
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  if (!appeal) {
    return <div style={{ padding: '20px' }}>Appeal not found.</div>;
  }

  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      maxWidth: '900px',
      margin: '0 auto',
      padding: '20px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        borderBottom: '2px solid #1a365d',
        paddingBottom: '15px'
      }}>
        <h1 style={{ color: '#1a365d', margin: 0 }}>Clinix AI</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => navigate('/appeal-history')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#edf2f7',
              color: '#1a365d',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Back to History
          </button>
          <button
            onClick={handleLogout}
            style={{
              padding: '8px 16px',
              backgroundColor: '#e53e3e',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{ color: '#2d3748', margin: 0 }}>
          {appeal.patient_name}
        </h2>
        <select
          value={appeal.status || 'pending'}
          onChange={(e) => handleStatusChange(e.target.value)}
          disabled={updating}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid #ddd',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="denied">Denied</option>
        </select>
      </div>

      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '10px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#1a365d', marginBottom: '16px' }}>Patient Information</h3>
        <p><strong>Date of Birth:</strong> {appeal.date_of_birth}</p>
        <p><strong>Diagnosis:</strong> {appeal.diagnosis}</p>
        <p><strong>Requested Medication:</strong> {appeal.requested_medication}</p>
        <p style={{ color: '#718096', fontSize: '13px' }}>
          <strong>Date Generated:</strong> {new Date(appeal.created_at).toLocaleString()}
        </p>
      </div>

      <div style={{
        backgroundColor: '#fff5f5',
        border: '1px solid #fed7d7',
        padding: '24px',
        borderRadius: '10px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#c53030', marginBottom: '12px' }}>
          Why It Was Denied — Plain English
        </h3>
        <p style={{ color: '#2d3748', lineHeight: '1.8' }}>
          {appeal.denial_explanation}
        </p>
      </div>

      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '10px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <h3 style={{ color: '#1a365d', margin: 0 }}>Generated Appeal Letter</h3>
          <button
            onClick={handleCopy}
            style={{
              padding: '8px 16px',
              backgroundColor: '#48bb78',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              minWidth: '70px'
            }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <p style={{ color: '#2d3748', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>
          {appeal.appeal_letter}
        </p>
      </div>
    </div>
  );
}

export default AppealDetail;